import { useEffect, useMemo, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Users, Search, Shield, X, Check, ScrollText, ExternalLink, UserPlus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import IgManageShell from "@/components/IgManageShell";

const TYPE_LABEL = { user: "User Page", admin: "Admin Page" };

export default function PagesAccess() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [fType, setFType] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [manage, setManage] = useState(null); // page being access-managed
  const [candidates, setCandidates] = useState([]);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantPerms, setGrantPerms] = useState({ can_view: true, can_manage: true, can_publish: false });
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await api.adminPagesList(); setPages(d.pages || []); } catch { toast.error("Couldn't load pages."); }
    setLoading(false);
  };
  useEffect(() => { load(); api.adminGrantCandidates().then((d) => setCandidates(d.users || [])).catch(() => {}); }, []);

  const filtered = useMemo(() => pages.filter((p) => {
    if (fType !== "all" && p.type !== fType) return false;
    if (fStatus === "published" && !p.published) return false;
    if (fStatus === "unpublished" && p.published) return false;
    if (q && !(`${p.name} ${p.route}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  }), [pages, q, fType, fStatus]);

  const togglePublish = async (p) => {
    try { const u = await api.adminPageUpdate(p.id, { published: !p.published }); setPages((x) => x.map((v) => v.id === p.id ? { ...v, published: u.published } : v)); toast.success(u.published ? "Published" : "Unpublished"); }
    catch { toast.error("Failed."); }
  };
  const toggleAccessControl = async (p) => {
    try { const u = await api.adminPageUpdate(p.id, { access_control: !p.access_control }); setPages((x) => x.map((v) => v.id === p.id ? { ...v, access_control: u.access_control } : v)); }
    catch { toast.error("Failed."); }
  };

  const openManage = (p) => { setManage(p); setGrantEmail(""); setGrantPerms({ can_view: true, can_manage: true, can_publish: false }); };
  const grant = async () => {
    if (!grantEmail.trim()) return toast.error("Pick or type a teammate email.");
    setBusy(true);
    try { await api.adminPageGrant(manage.id, { email: grantEmail.trim(), ...grantPerms }); toast.success("Access granted"); await load(); setManage((m) => pages.find((x) => x.id === m.id) || m); const d = await api.adminPagesList(); setPages(d.pages); setManage(d.pages.find((x) => x.id === manage.id)); setGrantEmail(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't grant access."); } finally { setBusy(false); }
  };
  const revoke = async (uid) => {
    try { await api.adminPageRevoke(manage.id, uid); const d = await api.adminPagesList(); setPages(d.pages); setManage(d.pages.find((x) => x.id === manage.id)); toast.success("Revoked"); }
    catch { toast.error("Failed."); }
  };

  const openAudit = async () => { setShowAudit(true); try { const d = await api.adminAuditLog(120); setAudit(d.entries || []); } catch {} };

  const pill = (on) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${on ? "bg-emerald-400/15 text-emerald-300" : "bg-white/5 text-[#666]"}`;
  const pillSky = (on) => `rounded-full px-2 py-0.5 text-[10px] font-bold ${on ? "bg-sky-400/15 text-sky-300" : "bg-white/5 text-[#666]"}`;
  const selCls = "rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-xs text-white outline-none";

  return (
    <IgManageShell need="admin" title="Pages & Access">
      <div data-testid="pages-access-page">
        <p className="mb-4 max-w-3xl text-sm text-[#A1A1AA]">One central place to publish/unpublish every app page and grant specific teammates access to management pages — without giving them full admin. Access is enforced server-side.</p>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative"><Search size={14} className="absolute left-2.5 top-2.5 text-[#666]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages…" data-testid="pa-search" className="w-56 rounded-lg border border-white/10 bg-black/30 py-2 pl-8 pr-3 text-xs text-white outline-none" /></div>
          <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="pa-filter-type" className={selCls}><option value="all">All types</option><option value="user">User pages</option><option value="admin">Admin pages</option></select>
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="pa-filter-status" className={selCls}><option value="all">All status</option><option value="published">Published</option><option value="unpublished">Unpublished</option></select>
          <button onClick={openAudit} data-testid="pa-audit-btn" className="ml-auto flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><ScrollText size={13} /> Audit Log</button>
        </div>

        {loading ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div> : (
          <div className="overflow-hidden rounded-xl border border-white/10" data-testid="pa-table">
            <div className="grid grid-cols-[2fr_1.6fr_1fr_1fr_1.2fr_auto] gap-2 border-b border-white/10 bg-white/[0.03] px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-[#71717a]">
              <span>Page</span><span>URL Slug</span><span>Type</span><span>Status</span><span>Access</span><span>Actions</span>
            </div>
            {filtered.map((p) => (
              <div key={p.id} data-testid={`pa-row-${p.slug}`} className="grid grid-cols-[2fr_1.6fr_1fr_1fr_1.2fr_auto] items-center gap-2 border-b border-white/5 px-3 py-2.5 text-sm">
                <div className="min-w-0"><div className="truncate font-bold text-white">{p.name}</div><div className="truncate text-[11px] text-[#666]">{p.description}</div></div>
                <a href={p.route} target="_blank" rel="noreferrer" className="flex items-center gap-1 truncate text-xs text-[#8B5CF6] hover:underline">{p.route} <ExternalLink size={11} /></a>
                <span className="text-xs text-[#A1A1AA]">{TYPE_LABEL[p.type]}</span>
                <button onClick={() => togglePublish(p)} data-testid={`pa-publish-${p.slug}`} className={pill(p.published)}>{p.published ? "Published" : "Unpublished"}</button>
                <div className="flex flex-wrap items-center gap-1">
                  {p.admin_only ? <span className="text-[10px] font-bold text-amber-300">Admin only</span> : (
                    <>
                      <button onClick={() => toggleAccessControl(p)} className={pillSky(p.access_control)} title="Restrict to granted users">{p.access_control ? "Restricted" : "Open"}</button>
                      {p.grants?.length > 0 && <span className="flex items-center gap-0.5 text-[10px] text-[#A1A1AA]"><Users size={10} /> {p.grants.length}</span>}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1.5 justify-self-end">
                  <button onClick={() => togglePublish(p)} title={p.published ? "Unpublish" : "Publish"} className="rounded p-1.5 text-[#A1A1AA] hover:text-white">{p.published ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                  {!p.admin_only && <button onClick={() => openManage(p)} data-testid={`pa-manage-${p.slug}`} className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-bold text-white hover:border-[#E1306C]/50"><Shield size={12} /> Manage Access</button>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manage Access modal */}
      {manage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="pa-manage-modal" onClick={() => setManage(null)}>
          <div className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-[#111214] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-1 flex items-center justify-between"><h3 className="text-base font-bold text-white">Manage Access — {manage.name}</h3><button onClick={() => setManage(null)}><X size={18} className="text-white/50 hover:text-white" /></button></div>
            <p className="mb-3 text-xs text-[#A1A1AA]">Grant a teammate access to this page only. They will NOT get any other admin section.</p>

            <div className="mb-3 rounded-lg border border-[#E1306C]/25 bg-white/[0.02] p-3">
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[200px] flex-1">
                  <label className="mb-1 block text-[10px] font-bold uppercase text-[#71717a]">Teammate</label>
                  <input list="pa-cands" value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder="email@example.com" data-testid="pa-grant-email" className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none" />
                  <datalist id="pa-cands">{candidates.map((u) => <option key={u.user_id} value={u.email}>{u.name}</option>)}</datalist>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-3">
                {["can_view", "can_manage", "can_publish"].map((k) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs font-semibold text-white"><input type="checkbox" checked={grantPerms[k]} onChange={(e) => setGrantPerms((p) => ({ ...p, [k]: e.target.checked }))} data-testid={`pa-perm-${k}`} /> {k.replace("can_", "").replace(/^./, (c) => c.toUpperCase())}</label>
                ))}
                <button onClick={grant} disabled={busy} data-testid="pa-grant-save" className="ml-auto flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{busy ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />} Grant</button>
              </div>
            </div>

            <div className="max-h-64 space-y-1.5 overflow-auto" data-testid="pa-grant-list">
              {(manage.grants || []).length === 0 && <div className="py-6 text-center text-xs text-[#666]">No one has been granted access yet.</div>}
              {(manage.grants || []).map((g) => (
                <div key={g.user_id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-2.5">
                  <div className="min-w-0 flex-1"><div className="truncate text-sm font-bold text-white">{g.email}</div><div className="text-[10px] text-[#666]">{g.name}</div></div>
                  {g.can_view && <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[9px] font-bold text-[#A1A1AA]">View</span>}
                  {g.can_manage && <span className="rounded-full bg-emerald-400/15 px-1.5 py-0.5 text-[9px] font-bold text-emerald-300">Manage</span>}
                  {g.can_publish && <span className="rounded-full bg-sky-400/15 px-1.5 py-0.5 text-[9px] font-bold text-sky-300">Publish</span>}
                  <button onClick={() => revoke(g.user_id)} data-testid={`pa-revoke-${g.user_id}`} className="rounded p-1 text-red-300 hover:text-red-400"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Audit log */}
      {showAudit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setShowAudit(false)}>
          <div className="relative w-full max-w-2xl rounded-2xl border border-white/10 bg-[#111214] p-5" onClick={(e) => e.stopPropagation()} data-testid="pa-audit-modal">
            <WindowSearch />
            <div className="mb-3 flex items-center justify-between"><h3 className="flex items-center gap-2 text-base font-bold text-white"><ScrollText size={16} /> Audit Log</h3><button onClick={() => setShowAudit(false)}><X size={18} className="text-white/50 hover:text-white" /></button></div>
            <div className="max-h-[60vh] space-y-1.5 overflow-auto">
              {audit.length === 0 && <div className="py-8 text-center text-xs text-[#666]">No activity yet.</div>}
              {audit.map((e) => (
                <div key={e.id} className="flex items-center gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs">
                  <span className="font-bold text-white">{e.user_name}</span>
                  <span className="rounded bg-[#E1306C]/15 px-1.5 py-0.5 font-semibold text-[#f48fb1]">{e.action}</span>
                  <span className="truncate text-[#A1A1AA]">{e.target_name}</span>
                  <span className="ml-auto whitespace-nowrap text-[10px] text-[#666]">{new Date(e.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </IgManageShell>
  );
}
